// import { ChildrenState } from '@smartsheet-dataflow/formjs';
import { Action, Dispatch, Reducer, StoreEnhancer } from 'redux';
import { BridgeFormJSState, DEFAULT_STATE } from './Types';
import { schemaSelector } from './features';
import { paramModelSelector, paramPreloadedState } from './params';
import { getParamFromSchema } from './params/ParamFromSchema';
import { ParamModel } from './params/ParamModelTypes';
import { ParamSchema } from './params/ParamSchemaTypes';

const ADD_INPUT_ACTION_TYPE = 'ADD_INPUT';
const REMOVE_INPUT_ACTION_TYPE = 'REMOVE_INPUT';

type ToModelHandler = (model: ParamModel) => void;

interface AddInputAction extends Action {
  type: typeof ADD_INPUT_ACTION_TYPE;
  payload: AddInputActionPayload;
}

interface AddInputActionPayload {
  parentId: string;
  inputId: string;
}

interface RemoveInputAction extends Action {
  type: typeof REMOVE_INPUT_ACTION_TYPE;
  payload: RemoveInputActionPayload;
}

interface RemoveInputActionPayload {
  parentId: string;
  inputId: string;
}

const isAddInputAction = (action: Action): action is AddInputAction =>
  action && action.type === ADD_INPUT_ACTION_TYPE;

const isRemoveInputAction = (action: Action): action is RemoveInputAction =>
  action && action.type === REMOVE_INPUT_ACTION_TYPE;

export const addInputActionCreator =
  (dispatch: Dispatch) => (payload: AddInputActionPayload) =>
    dispatch({ type: ADD_INPUT_ACTION_TYPE, payload });

export const removeInputActionCreator =
  (dispatch: Dispatch) => (payload: RemoveInputActionPayload) =>
    dispatch({ type: REMOVE_INPUT_ACTION_TYPE, payload });

export const addInputReducer: Reducer<BridgeFormJSState, AddInputAction> = (
  state,
  action
): BridgeFormJSState => {
  if (state !== undefined) {
    const runtimeSchema = schemaSelector(action.payload.parentId)(state);
    const newStateReducer = getParamFromSchema(action.payload.parentId, {});
    const newState = newStateReducer(state, {
      ...runtimeSchema,
      param: action.payload.inputId,
      allowArray: false,
      isOpen: true,
    });

    return {
      ...state,
      ...newState,
    };
  }
  return { ...DEFAULT_STATE };
};

// Define our own custom children state
type ChildrenState = {
  children: {
    [inputId: string]: string[];
  };
};

export const removeInputFromGroupReducer = (
  childrenValue: ChildrenState['children'],
  payload: RemoveInputActionPayload
): ChildrenState['children'] => {
  const inputValue = childrenValue[payload.parentId];

  const inputIdIndex = inputValue.findIndex(value => value === payload.inputId);
  if (inputIdIndex > -1) {
    const newInputValue = [
      ...inputValue.slice(0, inputIdIndex),
      ...inputValue.slice(inputIdIndex + 1),
    ];

    return { ...childrenValue, [payload.parentId]: newInputValue };
  }
  return { ...childrenValue };
};

export const removeInputFeaturesReducer = (
  state: BridgeFormJSState,
  payload: RemoveInputActionPayload
): BridgeFormJSState => {
  const returnState = Object.keys(state).reduce((accState, featureId) => {
    const returnFeature = Object.keys(state[featureId]).reduce(
      (accFeature, inputId) => {
        if (
          inputId === payload.inputId ||
          inputId.startsWith(`${payload.inputId}.`)
        ) {
          return accFeature;
        }
        return { ...accFeature, [inputId]: state[featureId][inputId] };
      },
      {}
    );

    return { ...accState, [featureId]: returnFeature };
  }, {});

  return { ...DEFAULT_STATE, ...state, ...returnState };
};

export const removeInputReducer: Reducer<
  BridgeFormJSState,
  RemoveInputAction
> = (state, action): BridgeFormJSState => {
  if (state !== undefined) {
    const children = removeInputFromGroupReducer(
      state.children,
      action.payload
    );

    return removeInputFeaturesReducer({ ...state, children }, action.payload);
  }
  return { ...DEFAULT_STATE };
};

export const bridgeSchemaEnhancer =
  (
    handler: ToModelHandler,
    schema: ParamSchema[],
    model: ParamModel
  ): StoreEnhancer<{}, BridgeFormJSState> =>
  createStore =>
  <S, A extends Action>(reducer: Reducer<S, A>) => {
    const wrappedFromSchemaReducer: Reducer<S & BridgeFormJSState, A> = (
      state,
      action
    ) => {
      const newState = reducer(state, action);

      if (state !== undefined && isAddInputAction(action)) {
        return { ...newState, ...addInputReducer(state, action) };
      } else if (state !== undefined && isRemoveInputAction(action)) {
        return { ...newState, ...removeInputReducer(state, action) };
      }

      return { ...DEFAULT_STATE, ...newState };
    };

    const initialState = paramPreloadedState(schema, model);

    const store = createStore(wrappedFromSchemaReducer, initialState as any);

    store.subscribe(() => {
      const newModel = paramModelSelector(store.getState());
      handler(newModel);
    });

    return store;
  };
